/**
 * Left-rail list of all 17 Gateway endpoints, grouped by category.
 *
 * Selection is controlled by parent (`EndpointExplorer`) so the
 * URL hash / response pane / form all stay in sync. No state here.
 */
import type { EndpointSpec, EndpointCategory } from '@/lib/types';
import { cn } from '@/lib/cn';
import { Badge } from './ui/badge';

const CATEGORY_LABEL: Record<EndpointCategory, string> = {
  ops: 'Ops sanity',
  storage: 'Attachments',
  contract: 'Case / Contract / Agreement',
  token: 'Token admin',
  subscriptions: 'Webhook subscriptions',
};

const CATEGORY_ORDER: EndpointCategory[] = ['ops', 'storage', 'contract', 'token', 'subscriptions'];

interface Props {
  endpoints: EndpointSpec[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function EndpointList({ endpoints, selectedId, onSelect }: Props) {
  const grouped = new Map<EndpointCategory, EndpointSpec[]>();
  for (const ep of endpoints) {
    const list = grouped.get(ep.category) ?? [];
    list.push(ep);
    grouped.set(ep.category, list);
  }

  return (
    <nav className="h-full overflow-y-auto px-2 py-3">
      {CATEGORY_ORDER.map((cat) => {
        const eps = grouped.get(cat);
        if (!eps || eps.length === 0) return null;
        return (
          <div key={cat} className="mb-4">
            <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase px-3 mb-1">
              {CATEGORY_LABEL[cat]}
            </div>
            <ul className="space-y-0.5">
              {eps.map((ep) => {
                const selected = ep.id === selectedId;
                return (
                  <li key={ep.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(ep.id)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 rounded-md transition-colors flex items-center gap-2 text-sm',
                        selected ? 'bg-brand text-white' : 'hover:bg-slate-100 text-slate-800',
                      )}
                    >
                      <span
                        className={cn(
                          'inline-block w-12 text-[10px] font-bold font-mono shrink-0',
                          selected ? 'text-white' : 'text-slate-500',
                        )}
                      >
                        {ep.method}
                      </span>
                      <span className="font-mono text-xs truncate flex-1">{ep.pathTemplate}</span>
                      {ep.signedSpec !== null && (
                        <Badge
                          variant={selected ? 'default' : 'info'}
                          className={cn('shrink-0', selected && 'bg-white/20 text-white')}
                          title="Body is canonicalized + ed25519-signed by emulator before forwarding"
                        >
                          signed
                        </Badge>
                      )}
                      {ep.auth === 'op+issuer' && (
                        <Badge
                          variant={selected ? 'default' : 'warning'}
                          className={cn('shrink-0', selected && 'bg-white/20 text-white')}
                          title="Requires X-Issuer-Token in addition to X-OP-API-Key"
                        >
                          issuer
                        </Badge>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}
